/*
 * Copyright (c) 2021.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Nikita Tropin <ntropin@yandex.ru>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  inject,
  Injector,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { AuthService } from 'compob2b-auth';
import {
  Cart,
  CartEventService,
  CartLocalService,
  CartProduct,
  CartProductReplace,
  CartRemoteService,
  ceilKopecks,
  CustomerGroup,
  FileService,
  ListTable,
  Settings,
  StoreService,
  Warehouse
} from 'compob2b-libs';
import { APIDefinition, Columns, Event as TableEvent } from 'compob2b-table';
import { forkJoin, Observable } from 'rxjs';
import { filter, map, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { translations } from '../../../../environments/translations.ru';
import { ModalProductComponent } from '../../product/modal-product/modal-product.component';
import { ProductService } from '../../product/services/product.service';
import { SpecificationPopupComponent } from '../specification-popup/specification-popup.component';
import { AddToCartManuallyComponent } from "../add-to-cart-manually/add-to-cart-manually.component";
import { ImportToCartComponent } from "../import-to-cart/import-to-cart.component";
import {
  SpecificationPopupFileComponent
} from "../../orders/specifications-module/components/popup-file/popup-file.component";
import { DeviceService } from "../../../../../../compob2b-libs/src/lib/services/device.service";

@Component({
  selector: 'app-cart-table',
  templateUrl: './cart-table.component.html',
  styleUrls: ['./cart-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CartTableComponent extends ListTable implements OnInit {
  cartProductList: CartProduct[];
  cartProductListFiltered: CartProduct[] = [];
  keyword = '';

  private cartService: Cart;
  readonly costVAT$ = this.cartEventService.getTotalCostWithVAT();
  readonly cost$ = this.cartEventService.getTotalCost();
  readonly costWithoutDiscount$ = this.cartEventService.getTotalCostWithoutDiscount();
  readonly discountCost$ = this.cartEventService.getDiscountTotalCost();
  readonly totalVolume$ = this.cartEventService.getTotalVolume();
  readonly totalWeight$ = this.cartEventService.getTotalWeight();
  totalCost = 0;
  totalCostVAT = 0;
  totalCostWithoutDiscount = 0;
  discountTotalCost = 0;
  totalVolume = 0;
  totalWeight = 0;
  showTotalVolume = false;
  showTotalWeight = false;
  settings: Settings;

  readonly cart$ = this.cartEventService.getCart();
  readonly customerGroup$ = this.storeService.getCustomerGroup();
  readonly replaceAnalog$ = this.storeService.getReplaceAnalog();
  canEdit = false;

  /**
   * True while `app-cart-order-block`'s confirmation modal is open — keeps the mobile-view
   * wrapper (gated on `cartProductList.length`) mounted after a successful submit clears the
   * placed rows out of the cart, otherwise the modal would be unmounted the instant it appears.
   */
  orderConfirmationVisible = false;

  productService = inject(ProductService);
  device = inject(DeviceService);

  /**
   * Drives which of the two `app-cart-order-block` mount points (desktop/mobile) is active — the
   * block must exist exactly once at a time, since mounting it fires HTTP requests (pickup/delivery
   * addresses) and can emit {@link CartOrderBlockComponent.deliveryAddressChange} on init.
   */
  readonly isMobile$: Observable<boolean> = inject(BreakpointObserver)
    .observe(['(max-width: 1023px)'])
    .pipe(map(result => result.matches), shareReplay(1));

  userDeselectedIds = new Set<number>();

  /** Delivery address currently pricing the cart; null means the ambient contract. */
  deliveryAddressId: number | null = null;
  /** True while a re-price round-trip is in flight — drives the table loader. */
  refreshing = false;

  onFoundProducts = new EventEmitter<CartProduct[]>();
  onSubmitProducts = new EventEmitter<boolean>();

  @ViewChild('table', {static: true}) table: APIDefinition;
  @ViewChild('photoTpl', {static: true}) photoTpl: TemplateRef<any>;
  @ViewChild('codeIconTpl', {static: true}) codeIconTpl: TemplateRef<any>;
  @ViewChild('headerTpl', {static: true}) headerTpl: TemplateRef<any>;
  @ViewChild('wmsTpl', {static: true}) wmsTpl: TemplateRef<any>;
  @ViewChild('priceTpl', {static: true}) priceTpl: TemplateRef<any>;
  @ViewChild('priceVATTpl', {static: true}) priceVATTpl: TemplateRef<any>;
  @ViewChild('cartTpl', {static: true}) cartTpl: TemplateRef<any>;
  @ViewChild('sumTpl', {static: true}) sumTpl: TemplateRef<any>;
  @ViewChild('sumVATTpl', {static: true}) sumTaxTpl: TemplateRef<any>;
  @ViewChild('removeTpl', {static: true}) removeTpl: TemplateRef<any>;
  @ViewChild('supplyTermTpl', {static: true}) supplyTermTpl: TemplateRef<any>;
  @ViewChild('deliveryAtpTpl', {static: true}) deliveryAtpTpl: TemplateRef<any>;

  constructor(injector: Injector,
              private readonly fileService: FileService,
              private readonly authService: AuthService,
              private readonly cartEventService: CartEventService,
              private readonly storeService: StoreService
  ) {
    super();

    this.settings = this.storeService.getSettings();

    this.cartService = this.authService.isAnonymous() && !this.shareSyncUid ?
      injector.get<Cart>(CartLocalService) :
      injector.get<Cart>(CartRemoteService);
    this.canEdit = !this.authService.isAnonymous();

    if (this.shareSyncUid) {
      this.cartService.getShareCart(this.shareSyncUid).pipe(takeUntil(this.unsubscribe)).subscribe();
      this.customerGroup$ = this.storeService.getSharedCartCustomerGroup(this.shareSyncUid).pipe(tap(response => this.storeService.setCustomerGroup(response)));
      this.customerGroup$ = this.storeService.getCustomerGroup();
    }
  }

  get shareSyncUid() {
    return this.route.snapshot.params['shareSyncUid'];
  }

  ngOnInit(): void {
    this.cost$.pipe(takeUntil(this.unsubscribe)).subscribe(totalCost => this.totalCost = totalCost);
    this.costVAT$.pipe(takeUntil(this.unsubscribe)).subscribe(total => this.totalCostVAT = total);
    this.costWithoutDiscount$.pipe(takeUntil(this.unsubscribe)).subscribe(totalCostWithoutDiscount => this.totalCostWithoutDiscount = totalCostWithoutDiscount);
    this.discountCost$.pipe(takeUntil(this.unsubscribe)).subscribe(discountTotalCost => this.discountTotalCost = discountTotalCost || 0);
    this.totalWeight$.pipe(takeUntil(this.unsubscribe)).subscribe(totalWeight => this.totalWeight = totalWeight);
    this.totalVolume$.pipe(takeUntil(this.unsubscribe)).subscribe(totalVolume => this.totalVolume = totalVolume);

    this.configuration.checkboxes = this.canEdit;
    this.configuration.paginationEnabled = false;
    this.configuration.serverPagination = false;

    this.customerGroup$.pipe(tap(data => this.handleCustomerGroup(data)), takeUntil(this.unsubscribe)).subscribe();

    this.cart$.pipe(tap(data => {
      this.cartProductList = [...data].sort((a, b) => a.id - b.id);
      this.syncSelectionsWithCart(data);
      this.onFilter();
    }), takeUntil(this.unsubscribe)).subscribe();

    this.replaceAnalog$.pipe(
      filter((val: CartProductReplace) => val !== null),
      switchMap((val: CartProductReplace) => this.replaceAnalog(val)),
      takeUntil(this.unsubscribe)
    ).subscribe(_ => this.cartEventService.nextReplaceCartProduct());

    this.onFoundProducts.pipe(
      tap((response: CartProduct[]) => this.openPopupImport(response)),
      takeUntil(this.unsubscribe)
    ).subscribe();
  }

  replaceAnalog(data: CartProductReplace): Observable<any> {
    return this.cartService.replace(data).pipe(
      tap(_ => this.storeService.nextReplaceAnalog(null))
    );
  }

  selectRow(row: any, value: boolean): void {
    if (value && !CartTableComponent.isAvailable(row)) {
      return;
    }
    super.selectRow(row, value);
    if (!value) {
      this.userDeselectedIds.add(row.id);
    } else {
      this.userDeselectedIds.delete(row.id);
    }
    this.cdr.markForCheck();
  }

  /** Sorting re-orders the table's rows and select-all ticks every position — both invalidate the checkmarks. */
  eventEmitted($event: { event: string; value: any; data?: any }): void {
    super.eventEmitted($event);
    if ($event.event === TableEvent.onOrder || $event.event === TableEvent.onSelectAll) {
      this.updateTableCheckboxVisuals();
    }
  }

  syncSelectionsWithCart(items: CartProduct[]): void {
    const cartIds = new Set(items.map(item => item.id));
    [...this.selected].forEach(id => {
      if (!cartIds.has(id)) {
        this.selected.delete(id);
        this.selectedRows.delete(id);
      }
    });
    [...this.userDeselectedIds].forEach(id => {
      if (!cartIds.has(id)) this.userDeselectedIds.delete(id);
    });
    items.forEach(item => {
      // A row without a price under the current contract must not be selectable: the totals treat a
      // missing price as 0 and would silently under-count it.
      if (!CartTableComponent.isAvailable(item)) {
        this.selected.delete(item.id);
        this.selectedRows.delete(item.id);
        return;
      }
      if (!this.userDeselectedIds.has(item.id)) {
        this.selected.add(item.id);
        this.selectedRows.set(item.id, item);
      }
    });
  }

  /**
   * The table keys its checkboxes by position in its OWN `data`, which the sort pipe re-orders in place
   * once a column is sorted — so positions are taken from that array (after a render has applied the
   * sort), never from {@link cartProductListFiltered}, otherwise checkmarks land on other rows than
   * the ones actually ordered.
   */
  updateTableCheckboxVisuals(): void {
    const tableComp = this.table as any;
    if (!tableComp?.selectedCheckboxes) return;
    tableComp.cdr?.detectChanges();
    tableComp.selectedCheckboxes.clear();
    (tableComp.data as CartProduct[] ?? []).forEach((item, index) => {
      if (this.selected.has(item.id)) {
        tableComp.selectedCheckboxes.add(index);
      }
    });
    tableComp.isAllSelected =
      this.cartProductListFiltered.length > 0 &&
      this.cartProductListFiltered.every(item => this.selected.has(item.id));
    tableComp.cdr?.detectChanges();
  }

  handleCustomerGroup(customerGroup: CustomerGroup) {
    this.productService.handleCustomerGroup(customerGroup);
    if (customerGroup) {
      this.columns = this.initTableColumns(customerGroup.warehouseGroup);
      this.tableService.initFrom(this);
    }
    this.loading = false;
  }

  protected initTableColumns(warehouses: Warehouse[]): Columns[] {
    const columns: Columns[] = [];
    columns.push({key: 'photo', title: translations.photo, cellTemplate: this.photoTpl});
    columns.push({
      key: 'header',
      title: translations.articulHeaderShowcase,
      orderEnabled: true,
      cellTemplate: this.headerTpl,
    });

    if (warehouses !== undefined && warehouses.length) {

      warehouses.forEach(warehouse =>
        columns.push({
          key: 'balancesOnGroupsOfWarehouses.' + warehouse.id,
          title: warehouse.header,
          cellTemplate: this.wmsTpl
        })
      );
    }

    columns.push({
      key: 'price',
      title: translations.priceRur,
      orderEnabled: true,
      cellTemplate: this.priceTpl,
      readonly: true
    });
    columns.push({
      key: 'priceVAT',
      title: translations.priceNdsRur,
      orderEnabled: true,
      cellTemplate: this.priceVATTpl,
      readonly: true
    });
    // Same slot as the catalog's readiness-date column: last one before "Количество".
    columns.push({
      key: 'deliveryAtp',
      title: translations.deliveryAtp,
      orderEnabled: true,
      cellTemplate: this.deliveryAtpTpl,
      readonly: true
    });
    columns.push({
      key: 'to_cart',
      title: translations.amount,
      cellTemplate: this.cartTpl,
      readonly: true
    });
    columns.push({key: 'sum', title: translations.sum, cellTemplate: this.sumTpl, readonly: true});
    columns.push({key: 'sumVAT', title: translations.sumTax, cellTemplate: this.sumTaxTpl, readonly: true});
    if (this.canEdit) {
      columns.push({key: 'remove', title: '', cellTemplate: this.removeTpl, readonly: true});
    }
    return columns;
  }

  public getImageUri(picture: string) {
    return picture && picture != '.' ? this.fileService.getImageSrc(picture, 800) : this.noPhotoSrc;
  }

  /**
   * Re-prices the cart under the contract of the chosen delivery address.
   *
   * <p>Cart-scoped by construction: the address id travels to the backend as a query parameter and
   * the resolved contract lives for that request only. Nothing here touches the stored customer.
   * A null id (pickup, or no address chosen) returns the cart to the ambient contract.
   */
  onOrderConfirmationVisibleChange(visible: boolean): void {
    this.orderConfirmationVisible = visible;

    if (!visible) {
      this.selectAllRemaining();
      return;
    }

    this.cdr.detectChanges();
  }

  /**
   * Selects every row still in the cart once the order confirmation is dismissed.
   *
   * <p>Placing an order removes the rows it covered, but whatever the customer had left unchecked
   * stays in {@link userDeselectedIds}, which {@link syncSelectionsWithCart} deliberately honours —
   * so the cart would come back with nothing selected and a zero total beside it. Clearing the set
   * lets the same sync re-select what is left; rows with no price under the current contract stay
   * out of it, since the totals cannot count them.
   *
   * <p>Same order of operations as the {@link cart$} subscription: sync, detect, then repaint the
   * table's own checkbox state.
   */
  private selectAllRemaining(): void {
    this.userDeselectedIds.clear();
    this.syncSelectionsWithCart(this.cartProductList ?? []);
    this.cdr.detectChanges();
    this.updateTableCheckboxVisuals();
  }

  onDeliveryAddressChange(deliveryAddressId: number | null): void {
    if (this.authService.isAnonymous()) {
      return;
    }

    this.deliveryAddressId = deliveryAddressId;
    this.refreshing = true;
    this.cdr.detectChanges();

    // The cart$ subscription re-runs syncSelectionsWithCart + updateTableCheckboxVisuals, so
    // selection and checkbox visuals stay in step with the re-priced rows.
    this.cartService.getByRegion(this.productService.customerGroup?.id, deliveryAddressId ?? undefined).pipe(
      take(1),
      takeUntil(this.unsubscribe)
    ).subscribe({
      next: () => {
        this.refreshing = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.refreshing = false;
        this.cdr.detectChanges();
      }
    });
  }

  /** Rows the invoice is requested for: selected and priced under the current contract. */
  get orderableSelectedRows(): CartProduct[] {
    return [...this.selectedRows.values()].filter(item => CartTableComponent.isAvailable(item));
  }

  /** Total unit count across the orderable selection, shown in the order block. */
  get selectedTotalAmount(): number {
    return this.orderableSelectedRows.reduce((sum, item) => sum + (item.amount ?? 0), 0);
  }

  /** A row with no price under the resolved contract stays visible but cannot be ordered. */
  static isAvailable(item: CartProduct): boolean {
    return item?.available !== false;
  }

  exportXlsFromBlock(): void {
    this.exportToXls();
  }

  getTotalCost(): number {
    return this.discountTotalCost !== 0 ? ceilKopecks(this.totalCost - this.discountTotalCost) : this.totalCost;
  }

  getSelectedTotalCost(): number {
    let totalCost = this.getSelectedTotalCostBase();
    return this.selectedRows.size > 0 && this.discountTotalCost !== 0
      ? ceilKopecks(totalCost - this.discountTotalCost) : totalCost;
  }

  /**
   * Accumulates the raw price times the amount and rounds once, the way {@code CartXlsxService}
   * builds the export's "Сумма:" cell — rounding each unit price first would drift a kopeck per
   * unit against the XLS this very cart exports.
   */
  getSelectedTotalCostBase(): number {
    let totalCost = 0;
    this.selectedRows.forEach((item: CartProduct) => {
      const price = item.discountPrice > 0 ? item.discountPrice : item.commercePrice?.price ?? 0;
      totalCost += price * item.amount;
    });
    return ceilKopecks(totalCost);
  }

  getSelectedTotalCostVATBase(): number {
    let totalCostVAT = 0;
    this.selectedRows.forEach((item: CartProduct) => {
      totalCostVAT += (item.commercePrice?.priceNds ?? 0) * item.amount;
    });
    return ceilKopecks(totalCostVAT);
  }

  get effectiveTotalCost(): number {
    return this.getSelectedTotalCostBase();
  }

  get effectiveTotalCostVAT(): number {
    return this.getSelectedTotalCostVATBase();
  }

  getFormattedPrice(value: number): { main: string; cents: string } {
    const rounded = Math.floor(value);
    const cents = Math.round((value - rounded) * 100);
    return {
      main: new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0, useGrouping: true }).format(rounded),
      cents: cents.toString().padStart(2, '0'),
    };
  }

  getTotalCostHint() {
    let hintText = 'С учетом скидки: %discount% ₽';
    let hintDiscount: number;
    if (this.selectedRows.size) {
      let selTotalCost = this.getSelectedTotalCost();
      let selTotalCostBase = this.getSelectedTotalCostBase();
      hintDiscount = selTotalCost !== selTotalCostBase ? selTotalCost - selTotalCostBase : 0;
    } else {
      hintDiscount = this.getTotalCost() !== this.totalCostWithoutDiscount ? this.getTotalCost() - this.totalCostWithoutDiscount : 0;
    }

    return hintDiscount !== 0 ? hintText.replace('%discount%', String(hintDiscount.toFixed(2))) : '';
  }

  openModalProduct(syncUid: string): void {
    if (!syncUid) return
    if (this.device.isMobile) {
      this.router.navigate(['/product/', syncUid]);
    } else {
      this.productService.openModalProduct(ModalProductComponent, syncUid);
    }
  }

  openSpecificationForm(allGoods: boolean = false) {
    const products = allGoods ? this.getCartProductIds() : Array.from(this.selected.values());

    this.windowService.open(SpecificationPopupComponent, {
      context: {products},
    });
  }

  private getCartProductIds(): number[] {
    const arr: number[] = [];
    this.cartProductList.forEach(row => {
      arr.push(row.id);
    });
    return arr;
  }

  remove(cartProduct: CartProduct): void {
    this.cartService.remove(cartProduct).pipe(take(1)).subscribe();
  }

  clearCartConfirm() {
    this.confirm(translations.confirmClearCart + '?', this.cartService.clear());
  }

  onFilter() {
    this.cartProductListFiltered =
      this.cartProductList.filter(item => this.filterCart(item));
    this.cdr.detectChanges();
    this.updateTableCheckboxVisuals();
  }

  private filterCart(item: CartProduct): boolean {
    const regex = new RegExp(this.keyword, 'i');
    const header = item.header?.search(regex) !== -1;
    const elcom = 'flatCodes' in item && item.flatCodes !== null &&
      item.flatCodes['elcom'] !== undefined &&
      item.flatCodes['elcom'].search(regex) !== -1;
    return header || elcom;
  }

  exportToXls() {
    if (this.selectedRows.size === 0) {
      return;
    }
    // Same source and shape as the cart GET (see CartRemoteService.getByRegion): the export must be
    // priced under whichever contract the screen is currently showing, not the ambient one. The
    // export is filtered server-side to just the selected rows (see CartXlsxService.generate()).
    const params = new URLSearchParams();
    if (this.deliveryAddressId) {
      params.set('deliveryAddressId', String(this.deliveryAddressId));
    }
    params.set('ids', Array.from(this.selectedRows.keys()).join(','));
    const url = '/cart/download?' + params.toString();
    this.fileService.downloadUrl(url).pipe(
      tap((response: Blob) => this.handleDownloadResponse(response)),
    ).subscribe();
  }

  handleDownloadResponse(response: Blob) {
    if (response.size !== 0) {
      FileService.saveAs(response, 'Cart.xlsx');
    }
  }

  protected openAddToCartManually() {
    this.windowService.open(AddToCartManuallyComponent, {
        context: {
          onFoundProducts: this.onFoundProducts
        },
        windowClass: 'manually-popup'
      }
    );
  }

  private openPopupImport(cartProductList: CartProduct[]) {
    this.windowService.open(ImportToCartComponent,
      {
        context: {
          cartProductList,
          onSubmitProducts: this.onSubmitProducts
        },
        windowClass: 'import-popup'
      }
    );
  }

  protected openAddToCartByFile() {
    this.windowService.open(SpecificationPopupFileComponent, {
        context: {
          onFoundProducts: this.onFoundProducts
        },
      }
    );
  }

  onCardCheckboxChange(row: CartProduct, event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectRow(row, input.checked);
  }

  isAllSelectedOnMobile(): boolean {
    return this.cartProductListFiltered.length > 0 &&
      this.cartProductListFiltered.every(item => this.selected.has(item.id));
  }

  toggleSelectAllOnMobile(): void {
    const allIds = this.cartProductListFiltered.map(item => item.id);
    const allSelected = this.isAllSelectedOnMobile();

    if (allSelected) {
      allIds.forEach(id => {
        this.selected.delete(id);
        this.userDeselectedIds.add(id);
        this.selectedRows.delete(id);
      });
    } else {
      allIds.forEach(id => {
        this.userDeselectedIds.delete(id);
        this.selected.add(id);
        const item = this.cartProductListFiltered.find(p => p.id === id);
        if (item) this.selectedRows.set(id, item);
      });
    }
    this.updateTableCheckboxVisuals();
    this.cdr.markForCheck();
  }

  removeSelectedOnMobile(): void {
    const selectedProducts = Array.from(this.selectedRows.values());
    if (selectedProducts.length === 0) return;

    const confirmMessage = 'Удалить выбранные товары?';
    const removalObservable = new Observable<void>(observer => {
      const deletions = selectedProducts.map(product => this.cartService.remove(product));
      forkJoin(deletions).pipe(take(1)).subscribe(() => {
        this.selected.clear();
        this.userDeselectedIds.clear();
        this.selectedRows.clear();
        observer.next();
        observer.complete();
      });
    });
    this.confirm(confirmMessage, removalObservable);
  }

  get mobileManageButtonText(): string {
    return this.selected.size > 0 ? 'Удалить выбранные' : 'Очистить корзину';
  }

  get mobileManageButtonAction(): () => void {
    return this.selected.size > 0 ? () => this.removeSelectedOnMobile() : () => this.clearCartConfirm();
  }

  trackByProductId(index: number, product: CartProduct): number {
    return product.id;
  }

  get showMobileFooter(): boolean {
    return this.selected.size > 0;
  }

  mobileMoreOpen = false;

  toggleMobileMoreMenu(event: Event): void {
    event.stopPropagation();
    this.mobileMoreOpen = !this.mobileMoreOpen;
    this.cdr.markForCheck();
  }

  closeMobileMoreMenu(): void {
    this.mobileMoreOpen = false;
    this.cdr.markForCheck();
  }

  onMobileMoreAction(action: 'spec' | 'xls' | 'clear'): void {
    this.closeMobileMoreMenu();
    switch (action) {
      case 'spec':
        this.openSpecificationForm(false);
        break;
      case 'xls':
        this.exportToXls();
        break;
      case 'clear':
        this.clearCartConfirm();
        break;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(ev: MouseEvent): void {
    if (!this.mobileMoreOpen) {
      return;
    }
    const t = ev.target as HTMLElement | null;
    if (t?.closest('.mobile-more-wrap')) {
      return;
    }
    this.closeMobileMoreMenu();
  }
}
